import { z } from "zod"
import { ExtendedSentimentEnum, SentimentEnum } from "../shared/common"
import { AnomalyImpactEnum, AnomalySeverityEnum, AnomalyTypeEnum } from "./signals"

export const ClusterForEnum = {
  Responses: "Responses",
  Questions: "Questions",
  Question: "Question",
  Issue: "Issue",
  ActionRequest: "ActionRequest",
} as const

export const ClusterByEnum = {
  Text: "Text",
  Subject: "Subject",
} as const

export const ClusterSegmentEnum = {
  all: "all",
  advocates: "advocates",
  neutrals: "neutrals",
  detractors: "detractors",
} as const

export const ClusterDomainEnum = {
  VoiceOfCustomer: "VoiceOfCustomer",
  Support: "Support",
  Goal: "Goal",
  Question: "Question",
  Issue: "Issue",
  ActionRequest: "ActionRequest",
} as const

export const FeedbackClusterDomainEnum = {
  VoiceOfCustomer: "VoiceOfCustomer",
} as const

export const SupportClusterDomainEnum = {
  Support: "Support",
} as const

export type ClusterBy = keyof typeof ClusterByEnum
export type ClusterFor = keyof typeof ClusterForEnum
export type ClusterSegment = keyof typeof ClusterSegmentEnum

export type ClusterDomain = keyof typeof ClusterDomainEnum
export type SupportClusterDomain = keyof typeof SupportClusterDomainEnum
export type FeedbackClusterDomain = keyof typeof FeedbackClusterDomainEnum

export const ClusterForSchema = z.enum(ClusterForEnum)
export const ClusterSegmentSchema = z.enum(ClusterSegmentEnum)
export const ClusterDomainSchema = z.enum(ClusterDomainEnum)

export const ComparableMetricSchema = z.object({
  total: z.number().optional(),
  current: z.number().optional(),
  previous: z.number().optional(),
  deltaPercentage: z.number().optional(),
})

export const GetNpsResponseDataSchema = z.object({
  score: z.number().min(-100).max(100).optional(),
  previousScore: z.number().min(-100).max(100).optional(),
  count: z.number().optional(),
  distribution: z.array(
    z
      .object({
        period: z.object({
          from: z.date().transform((value) => value.toISOString()),
          to: z.date().transform((value) => value.toISOString()),
        }),
        count: z.number(),
        score: z.number().min(-100).max(100).optional(),
        promoters: z.number(),
        detractors: z.number(),
        passives: z.number(),
      })
      .optional(),
  ),
})

export const GetSentimentResponseDataSchema = z.object({
  overallSentiment: z.number().optional().nullable(),
  previousOverallSentiment: z.number().optional().nullable(),
  count: z.number(),
  sentiment: z.array(
    z.object({
      date: z
        .date()
        .transform((value) => (value ? value.toISOString() : null))
        .nullable(),
      avgSentiment: z.number().nullish(),
      [SentimentEnum.VeryPositive]: z.number(),
      [SentimentEnum.Positive]: z.number(),
      [SentimentEnum.Neutral]: z.number(),
      [SentimentEnum.Negative]: z.number(),
      [SentimentEnum.VeryNegative]: z.number(),
    }),
  ),
})

export const GetVoiceOfCustomerResponseDataSchema = z.object({
  isCached: z.boolean(),
  clusters: z.array(
    z.object({
      key: z.string(),
      label: z.object({ short: z.string(), long: z.string() }).optional(),
      count: z.number(),
      [SentimentEnum.VeryNegative]: z.number(),
      [SentimentEnum.Negative]: z.number(),
      [SentimentEnum.Neutral]: z.number(),
      [SentimentEnum.Positive]: z.number(),
      [SentimentEnum.VeryPositive]: z.number(),
    }),
  ),
})

export const GetClustersResponseDataSchema = z.object({
  isCached: z.boolean(),
  clusters: z.array(
    z.object({
      key: z.string(),
      label: z.object({ short: z.string(), long: z.string() }).optional(),
      conversationsCount: z.number().nullish(),
      averageSentiment: z.number().nullish(),
      itemsCount: z.number().nullish(),
    }),
  ),
})

export const GetResponsesDistributionResponseDataSchema = z.object({
  count: z.number(),
  distribution: z.array(
    z.object({
      optionValue: z.string(),
      goalOptionId: z.string(),
      count: z.number(),
    }),
  ),
})

export const DailyStackedActivityTopSchema = z.object({
  top1: z.object({ id: z.string(), name: z.string() }).nullable(),
  top2: z.object({ id: z.string(), name: z.string() }).nullable(),
  top3: z.object({ id: z.string(), name: z.string() }).nullable(),
  top4: z.object({ id: z.string(), name: z.string() }).nullable(),
})

export const DailyActivityDataSchema = z.array(
  z.object({
    date: z
      .date()
      .transform((value) => (value ? value.toISOString() : null))
      .nullable(),
    baseValue: z.number(),
    topValue: z.number(),
    extraValue: z.number().optional(),
    rate: z.number().optional(),
  }),
)

export const DailyStackedActivityDataSchema = z.array(
  z.object({
    date: z
      .date()
      .transform((value) => (value ? value.toISOString() : null))
      .nullable(),
    top1: z.number().optional(),
    top2: z.number().optional(),
    top3: z.number().optional(),
    top4: z.number().optional(),
    others: z.number().optional(),
  }),
)

export const DailySentimentActivityDataSchema = z.array(
  z.object({
    date: z
      .date()
      .transform((value) => (value ? value.toISOString() : null))
      .nullable(),
    totalCount: z.number(),
    avgSentiment: z.number().nullable(),
    totalResponsesCount: z.number().optional(),
    [ExtendedSentimentEnum.VeryPositive]: z.number(),
    [ExtendedSentimentEnum.Positive]: z.number(),
    [ExtendedSentimentEnum.NeutralP]: z.number(),
    [ExtendedSentimentEnum.NeutralN]: z.number(),
    [ExtendedSentimentEnum.Negative]: z.number(),
    [ExtendedSentimentEnum.VeryNegative]: z.number(),
  }),
)

export const DailyNpsActivityDataSchema = z.array(
  z.object({
    date: z
      .date()
      .transform((value) => (value ? value.toISOString() : null))
      .nullable(),
    totalCount: z.number(),
    score: z.number(),
    promoters: z.number(),
    passives: z.number(),
    detractors: z.number(),
  }),
)

export const DailySimpleActivityDataSchema = z.array(
  z.object({
    date: z
      .date()
      .transform((value) => (value ? value.toISOString() : null))
      .nullable(),
    baseValue: z.number(),
  }),
)

export const GetActivityResponseDataSchema = z.object({
  achieveRate: z.number().optional(),
  dailyCount: DailyActivityDataSchema,
})

export const GetSupportActivityResponseDataSchema = z.object({
  autoSolveRate: z.number().optional(),
  dailyCount: DailyActivityDataSchema,
})

export const GetNpsActivityResponseDataSchema = z.object({
  dailyCount: DailyNpsActivityDataSchema,
})

export const GetBusinessOpportunitiesActivityResponseDataSchema = z.object({
  dailyCount: DailySimpleActivityDataSchema,
})

export const GetInsightsActivityResponseDataSchema = z.object({
  activeConversations: z.number(),
  engageRate: z.number().optional(),
  conversionRate: z.number().optional(),
  dailyCount: z.array(
    z.object({
      date: z
        .date()
        .transform((value) => (value ? value.toISOString() : null))
        .nullable(),
      goals: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          responses: z.number(),
        }),
      ),
    }),
  ),
  stackedDailyCount: DailyStackedActivityDataSchema,
  topContributors: DailyStackedActivityTopSchema,
})

export const GetSentimentActivityResponseDataSchema = z.object({
  dailyCount: DailySentimentActivityDataSchema,
})

export const GetTopicSupportActivityResponseDataSchema = z.object({
  domain: z.literal(ClusterDomainEnum.Support),
  dailyCount: DailyActivityDataSchema,
  allTimeSolvedCount: z.number(),
  allTimePotentialSolvedCount: z.number(),
  allTimeConversationsCount: z.number(),
  allTimeTicketConversationsCount: z.number(),
})

export const GetTopicVocActivityResponseDataSchema = z.object({
  domain: z.literal(ClusterDomainEnum.VoiceOfCustomer),
  dailyCount: DailySentimentActivityDataSchema,
  allTimeInsightsCount: z.number(),
  allTimeAvgSentiment: z.number().nullable(),
})

export const GetTopicGoalActivityResponseDataSchema = z.object({
  domain: z.literal(ClusterDomainEnum.Goal),
  dailyCount: DailySentimentActivityDataSchema,
  allTimeInsightsCount: z.number(),
  allTimeAvgSentiment: z.number().nullable(),
  allTimeCustomersCount: z.number(),
  allTimeCampaignCustomersCount: z.number().nullable(),
})

export const GetTopicQuestionActivityResponseDataSchema = z.object({
  domain: z.literal(ClusterDomainEnum.Question),
  dailyCount: DailyActivityDataSchema,
  allTimeCount: z.number(),
  allTimeAnsweredCount: z.number(),
  allTimeKnowledgeGapCount: z.number(),
})

export const GetTopicIssueActivityResponseDataSchema = z.object({
  domain: z.literal(ClusterDomainEnum.Issue),
  dailyCount: DailyActivityDataSchema,
  allTimeCount: z.number(),
  allTimeHighUrgencyCount: z.number(),
})

export const GetTopicActionRequestActivityResponseDataSchema = z.object({
  domain: z.literal(ClusterDomainEnum.ActionRequest),
  dailyCount: DailyActivityDataSchema,
  allTimeCount: z.number(),
  allTimeFulfilledCount: z.number(),
  allTimeEligibilityGapCount: z.number(),
})

export const GetTopicActivityResponseDataSchema = z.discriminatedUnion("domain", [
  GetTopicSupportActivityResponseDataSchema,
  GetTopicVocActivityResponseDataSchema,
  GetTopicGoalActivityResponseDataSchema,
  GetTopicQuestionActivityResponseDataSchema,
  GetTopicIssueActivityResponseDataSchema,
  GetTopicActionRequestActivityResponseDataSchema,
])

export const GetCampaignActivityResponseDataSchema = z.object({
  dailyCount: z.array(
    z.object({
      date: z
        .date()
        .transform((value) => (value ? value.toISOString() : null))
        .nullable(),
      reached: z.number(),
      engaged: z.number(),
    }),
  ),
  responsesCaptured: z.number(),
})

export const GetPredefinedResponsesActivityResponseDataSchema = z.object({
  totalOptionValues: z.number(),
  dailyCount: z.array(
    z.object({
      date: z
        .date()
        .transform((value) => (value ? value.toISOString() : null))
        .nullable(),
      goals: z.array(
        z.object({
          optionValue: z.string(),
          responses: z.number(),
        }),
      ),
    }),
  ),
})

export const CustomersMetricResponseDataSchema = z.object({
  impacted: ComparableMetricSchema,
  engaged: ComparableMetricSchema,
  achieved: ComparableMetricSchema,
})

export const GetDeflectionActivityResponseDataSchema = z.object({
  dailyCount: DailyActivityDataSchema,
})

export const GetIssueDeflectionActivityResponseDataSchema = z.object({
  dailyCount: DailyActivityDataSchema,
})

export const GetQuestionsActivityResponseDataSchema = z.object({
  dailyCount: DailyActivityDataSchema,
})

export const GetActionRequestsActivityResponseDataSchema = z.object({
  dailyCount: DailyActivityDataSchema,
})

export const ClusterSnapshotDataSchema = z.object({
  id: z.union([z.bigint(), z.number(), z.string()]).transform(String),
  canonicalTopicId: z
    .union([z.bigint(), z.number(), z.string()])
    .nullable()
    .transform((value) => (value ? value.toString() : null)),
  label: z.string(),
  description: z.string().nullable(),
  domain: z.enum(ClusterDomainEnum),
  goalId: z.bigint().nullable(),
  from: z.string(),
  to: z.string(),
  overallSentiment: z.number().nullable(),
  overallSolveRate: z.number().nullable(),
  potentialSolveRate: z.number().nullish(),
  conversationsCount: z.number().nullable(),
  customersCount: z.number().nullable(),
  itemsCount: z.number(),
})

export const CanonicalTopicDataSchema = z.object({
  id: z.union([z.bigint(), z.number(), z.string()]).transform(String),
  label: z.string(),
  description: z.string().nullable(),
  domain: z.enum(ClusterDomainEnum),
  goalId: z.bigint().nullable(),
  previousLabels: z.array(z.string()).nullable().default([]),
  snapshotIds: z.array(z.bigint()).nullable().default([]),
  snapshotLabels: z.array(z.string()).nullable().default([]),
  avgSentiment: z.number().nullable().default(null),
  avgSolveRate: z.number().nullable().default(null),
})

export const CanonicalTopicCompressionDataSchema = z.object({
  id: z.union([z.bigint(), z.number(), z.string()]).transform(String),
  label: z.string(),
  description: z.string(),
  customersCount: z.number().nullable(),
  conversationsCount: z.number().nullable(),
  totalItems: z.number(),
  snapshotCount: z.number(),
  snapshotLabels: z.array(z.string()).nullable().default([]),
  snapshotIds: z.array(z.bigint()).nullable().default([]),
  avgSentiment: z.number().nullable().default(null),
  avgSolveRate: z.number().nullable().default(null),
  streakInWeeks: z.number().default(0),
  avgPotentialSolveRate: z.number().nullable().default(null),
  isNewTopic: z.boolean().default(false),
})

export const CanonicalTopicsDataSchema = z.array(CanonicalTopicDataSchema)
export const CanonicalTopicsCompressionDataSchema = z.array(CanonicalTopicCompressionDataSchema)

export const CanonicalTopicsRankingDataSchema = z.object({
  canonicalTopicId: z.string(),
  label: z.string(),
  totalItems: z.number(),
  share: z.number().nullable(),
  rank: z.number().nullable(),
  snapshots: z.array(ClusterSnapshotDataSchema),
})

export const CANONICAL_RANKING_TOP_N = 8
export const UNCATEGORIZED_CANONICAL_TOPIC_ID = "uncategorized"

export const CanonicalTopicsTimelineDataSchema = z.array(
  z.object({
    date: z.date().transform((value) => value.toISOString()),
    periodTotalItems: z.number(),
    rankings: z.array(CanonicalTopicsRankingDataSchema),
  }),
)

export const CanonicalTopicsOverallRankingDataSchema = z.object({
  canonicalTopicId: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  rank: z.number(),
  totalItems: z.number(),
  share: z.number().nullable(),
  avgSentiment: z.number().nullable(),
  avgSolveRate: z.number().nullable(),
})

export const CanonicalTopicGroupedDataSchema = z.object({
  id: z.union([z.bigint(), z.number(), z.string()]).transform(String),
  label: z.string(),
  description: z.string(),
  totalItems: z.number(),
  customersCount: z.number().optional().nullable(),
  conversationsCount: z.number().optional().nullable(),
  avgSentiment: z.number().nullable(),
  avgSolveRate: z.number().nullable(),
  snapshotLabels: z.array(z.string()).nullable().default([]),
  snapshotIds: z.array(z.bigint()).nullable().default([]),
  snapshotCount: z.number(),
  streakInWeeks: z.number(),
  avgPotentialSolveRate: z.number().nullable(),
  isNewTopic: z.boolean(),
})

export const CanonicalTopicsGroupedDataSchema = z.object({
  groups: z.array(
    z.object({
      groupId: z.bigint().transform(String),
      groupName: z.string(),
      canonicalTopics: z.array(CanonicalTopicGroupedDataSchema),
    }),
  ),
  generatingSnapshots: z.boolean(),
})

export const CanonicalTopicsTimelineResponseDataSchema = z.object({
  overallRanking: z.record(z.string(), CanonicalTopicsOverallRankingDataSchema),
  timeline: CanonicalTopicsTimelineDataSchema,
})

export const WeeklyActivityResponseDataSchema = z.object({
  weeklyCounts: z.array(
    z.object({
      entityId: z.string(),
      weeklyCount: z.array(
        z.object({
          date: z
            .date()
            .transform((value) => value.toISOString())
            .nullable(),
          count: z.number(),
        }),
      ),
    }),
  ),
})

export const InsightSignalSchema = z.object({
  id: z.union([z.bigint(), z.number(), z.string()]).transform(String),
  detectedAt: z.date(),
  anomalyType: z.enum(AnomalyTypeEnum),
  severity: z.enum(AnomalySeverityEnum),
  impact: z.enum(AnomalyImpactEnum),
  metricPath: z.string(),
  label: z.string(),
  description: z.string(),
  baselineValue: z
    .any() // Decimal
    .transform((val) => (val ? Number(val) : null))
    .nullable(),
  currentValue: z
    .any() // Decimal
    .transform((val) => (val ? Number(val) : null))
    .nullable(),
  isSolved: z.boolean(),
})

export const GetSignalsResponseDataSchema = z.array(InsightSignalSchema)
